<template>
  <div class="track-photo-form">
    <label class="track-photo-form-label">
      Фотографии маршрута
      <input
        type="file"
        accept="image/jpeg,image/png,image/webp"
        multiple
        hidden
        ref="fileInput"
        @change="onFilesSelected"
      />
    </label>
    <button type="button" class="btn btn-secondary btn-sm" @click="fileInput?.click()">
      Добавить фото
    </button>

    <div v-if="existingPhotos.length || pendingPhotos.length" class="track-photo-previews">
      <div
        v-for="photo in existingPhotos"
        :key="`existing-${photo.id}`"
        class="track-photo-preview"
        :class="{ 'track-photo-preview--removed': removedPhotoIds.includes(photo.id) }"
      >
        <img :src="photo.thumb_url || photo.url" :alt="photo.filename || 'Фото'" />
        <button type="button" class="track-photo-remove" @click="toggleRemoveExisting(photo.id)">
          {{ removedPhotoIds.includes(photo.id) ? 'Вернуть' : 'Удалить' }}
        </button>
        <p v-if="photo.lat != null && photo.lng != null" class="track-photo-gps">GPS</p>
      </div>

      <div v-for="(photo, index) in pendingPhotos" :key="`pending-${index}`" class="track-photo-preview">
        <img :src="photo.previewUrl" :alt="photo.file.name" />
        <button type="button" class="track-photo-remove" @click="removePending(index)">Удалить</button>
        <p class="track-photo-gps">{{ photo.gps ? 'GPS найден' : 'GPS не найден' }}</p>
      </div>
    </div>

    <p v-if="error" class="error-message">{{ error }}</p>
  </div>
</template>

<script setup>
import { onBeforeUnmount, ref, watch } from 'vue'
import { extractGpsFromFile } from '../utils/imageGps'

const MAX_PHOTOS = 50

const props = defineProps({
  existingPhotos: {
    type: Array,
    default: () => [],
  },
})

const pendingPhotos = defineModel('pendingPhotos', {
  type: Array,
  default: () => [],
})

const removedPhotoIds = defineModel('removedPhotoIds', {
  type: Array,
  default: () => [],
})

const fileInput = ref(null)
const error = ref('')

watch(
  () => props.existingPhotos,
  () => {
    error.value = ''
  }
)

async function onFilesSelected(event) {
  const files = Array.from(event.target.files || [])
  event.target.value = ''
  if (!files.length) return

  error.value = ''

  const activeExisting = props.existingPhotos.filter(
    (photo) => !removedPhotoIds.value.includes(photo.id)
  ).length
  const total = activeExisting + pendingPhotos.value.length + files.length

  if (total > MAX_PHOTOS) {
    error.value = `Не больше ${MAX_PHOTOS} фотографий`
    return
  }

  const added = await Promise.all(
    files.map(async (file) => {
      const gps = await extractGpsFromFile(file)
      return {
        file,
        previewUrl: URL.createObjectURL(file),
        gps,
      }
    })
  )

  pendingPhotos.value = [...pendingPhotos.value, ...added]
}

function removePending(index) {
  const photo = pendingPhotos.value[index]
  if (photo?.previewUrl) URL.revokeObjectURL(photo.previewUrl)
  pendingPhotos.value = pendingPhotos.value.filter((_, i) => i !== index)
}

function toggleRemoveExisting(photoId) {
  if (removedPhotoIds.value.includes(photoId)) {
    removedPhotoIds.value = removedPhotoIds.value.filter((id) => id !== photoId)
  } else {
    removedPhotoIds.value = [...removedPhotoIds.value, photoId]
  }
}

onBeforeUnmount(() => {
  pendingPhotos.value.forEach((photo) => {
    if (photo.previewUrl) URL.revokeObjectURL(photo.previewUrl)
  })
})

defineExpose({
  photoMetadata() {
    return pendingPhotos.value.map((photo) =>
      photo.gps ? { lat: photo.gps.lat, lng: photo.gps.lng } : {}
    )
  },
  pendingFiles() {
    return pendingPhotos.value.map((photo) => photo.file)
  },
})
</script>

<style scoped>
.track-photo-form {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.track-photo-form-label {
  display: none;
}

.track-photo-previews {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.track-photo-preview {
  position: relative;
  width: 96px;
}

.track-photo-preview img {
  display: block;
  width: 96px;
  height: 72px;
  object-fit: cover;
  border-radius: 0.375rem;
}

.track-photo-preview--removed img {
  opacity: 0.35;
}

.track-photo-remove {
  display: block;
  margin-top: 0.25rem;
  padding: 0;
  border: none;
  background: transparent;
  color: #64748b;
  font-size: 0.75rem;
  cursor: pointer;
}

.track-photo-gps {
  margin: 0.125rem 0 0;
  font-size: 0.75rem;
  color: #64748b;
}
</style>
